/**
 * @file IntegrationKindCard.tsx
 * @description Карточка вида интеграции на странице «Интеграции»
 * (phase19.29, второй проход: вынесена из страницы, чтобы та уложилась в
 * 200 строк).
 *
 * Шапка — название и описание вида из реестра `INTEGRATION_KIND_META` и
 * «Добавить экземпляр»; ниже — экземпляры (`IntegrationInstanceRow`) или
 * «Не подключено».
 */

import { Button } from "../ui/button";
import { EditorialCard } from "../ui/EditorialCard";
import { Separator } from "../ui/separator";
import { IntegrationInstanceRow } from "./IntegrationInstanceRow";
import {
  INTEGRATION_KIND_META,
  type PlatformIntegrationDto,
  type PlatformIntegrationKind,
} from "../../lib/api-platform-integrations";

interface Props {
  kind: PlatformIntegrationKind;
  items: PlatformIntegrationDto[];
  expandedUsageId: string | null;
  testingId: string | null;
  onAdd: () => void;
  onEdit: (item: PlatformIntegrationDto) => void;
  onTest: (item: PlatformIntegrationDto) => void;
  onToggleUsage: (item: PlatformIntegrationDto) => void;
  onDelete: (item: PlatformIntegrationDto) => void;
  /** Перенос общей интеграции в магазин; нет — кнопки нет. */
  onTransfer?: ((item: PlatformIntegrationDto) => void) | null;
}

export function IntegrationKindCard({
  kind,
  items,
  expandedUsageId,
  testingId,
  onAdd,
  onEdit,
  onTest,
  onToggleUsage,
  onDelete,
  onTransfer,
}: Props) {
  const meta = INTEGRATION_KIND_META[kind];
  return (
    <EditorialCard>
      <EditorialCard.Body>
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0 flex-1">
            <h2 className="type-card-title text-on-surface">{meta.label}</h2>
            <p className="text-sm text-on-surface-variant">{meta.description}</p>
          </div>
          <Button variant="soft" onClick={onAdd}>
            Добавить экземпляр
          </Button>
        </div>

        {items.length === 0 ? (
          <p className="mt-3 text-sm text-on-surface-variant">Не подключено</p>
        ) : (
          <div className="mt-3 flex flex-col">
            {items.map((item, idx) => (
              <div key={item.id}>
                {idx > 0 ? <Separator /> : null}
                <IntegrationInstanceRow
                  item={item}
                  expanded={expandedUsageId === item.id}
                  testing={testingId === item.id}
                  testBusy={testingId !== null}
                  onEdit={() => onEdit(item)}
                  onTest={() => onTest(item)}
                  onToggleUsage={() => onToggleUsage(item)}
                  onDelete={() => onDelete(item)}
                  onTransfer={onTransfer ? () => onTransfer(item) : undefined}
                />
              </div>
            ))}
          </div>
        )}
      </EditorialCard.Body>
    </EditorialCard>
  );
}
